/**
 * News / Updates Section with masonry layout and filter tabs + Get Started CTA
 */
import { useState } from 'react';
import { motion } from 'motion/react';
import { Calendar, User, ArrowRight } from 'lucide-react';
import { content } from '@/landing/data/content';

type NewsCategory = 'All' | 'Press Release' | 'Article';

interface NewsProps {
  onGetStarted?: () => void;
}

const newsImages = [
  'https://images.unsplash.com/photo-1733925457822-64c3e048fa1b?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzdGFydHVwJTIwYW5ub3VuY2VtZW50fGVufDF8fHx8MTc3MTQ5ODAxOHww&ixlib=rb-4.1.0&q=80&w=1080',
  'https://images.unsplash.com/photo-1760199789464-eff5ba507e32?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx0ZWNobm9sb2d5JTIwbmV3cyUyMGFydGljbGV8ZW58MXx8fHwxNzcxNDk1NzMzfDA&ixlib=rb-4.1.0&q=80&w=1080',
  'https://images.unsplash.com/photo-1631599143419-ea8539ed4fbd?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx1bml2ZXJzaXR5JTIwY2FtcHVzJTIwYnVpbGRpbmd8ZW58MXx8fHwxNzcxNDI5MTA5fDA&ixlib=rb-4.1.0&q=80&w=1080',
];

export function News({ onGetStarted }: NewsProps) {
  const [selectedCategory, setSelectedCategory] = useState<NewsCategory>('All');

  const categories: NewsCategory[] = ['All', 'Press Release', 'Article'];

  const filteredNews = selectedCategory === 'All'
    ? content.news
    : content.news.filter(item => item.category === selectedCategory);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <section id="news" className="relative py-24 lg:py-32 bg-[var(--navy)] border-t border-[rgba(201,168,76,0.15)]">
      <div className="absolute inset-0 hero-grid-overlay opacity-40" />
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="font-display text-4xl md:text-5xl font-semibold text-[var(--gold)] mb-4">
            Latest News & Updates
          </h2>
          <p className="text-xl text-[var(--text-muted)] max-w-3xl mx-auto font-light">
            Stay informed about our latest developments and industry insights
          </p>
        </motion.div>

        {/* Filter Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="flex flex-wrap justify-center gap-3 mb-12"
        >
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                selectedCategory === category
                  ? 'bg-[var(--gold)] text-[var(--navy)] shadow-lg scale-105'
                  : 'bg-white/10 text-white/80 hover:bg-white/20 border border-white/10'
              }`}
            >
              {category}
            </button>
          ))}
        </motion.div>

        {/* News Grid */}
        <motion.div
          key={selectedCategory}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {filteredNews.map((item, index) => (
            <motion.article
              key={item.title}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              whileHover={{ y: -8 }}
              className={`group relative bg-[rgba(13,31,60,0.8)] rounded-2xl overflow-hidden border border-[rgba(255,255,255,0.05)] hover:border-[rgba(201,168,76,0.2)] transition-all duration-500 ${
                item.featured ? 'md:col-span-2 lg:col-span-2' : ''
              }`}
            >
              {/* Image */}
              <div className={`relative overflow-hidden ${item.featured ? 'h-80' : 'h-56'}`}>
                <motion.img
                  src={newsImages[index % newsImages.length]}
                  alt={item.title}
                  className="w-full h-full object-cover"
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.6 }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

                {/* Category Badge */}
                <div className="absolute top-4 left-4">
                  <span className={`px-4 py-2 rounded-full text-xs font-bold backdrop-blur-sm ${
                    item.category === 'Press Release'
                      ? 'bg-[var(--royal)] text-white'
                      : 'bg-[var(--gold)] text-[var(--navy)]'
                  }`}>
                    {item.category}
                  </span>
                </div>

                {/* Featured Badge */}
                {item.featured && (
                  <div className="absolute top-4 right-4">
                    <span className="px-4 py-2 rounded-full text-xs font-bold bg-amber-500/90 text-white backdrop-blur-sm">
                      Featured
                    </span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Meta Info */}
                <div className="flex items-center gap-4 text-sm text-[var(--text-muted)] mb-3">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(item.date)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    <span>{item.author}</span>
                  </div>
                </div>

                {/* Title */}
                <h3 className={`font-display font-semibold text-white mb-3 group-hover:text-[var(--gold)] transition-colors duration-300 ${
                  item.featured ? 'text-2xl' : 'text-xl'
                }`}>
                  {item.title}
                </h3>

                {/* Excerpt */}
                <p className="text-[var(--text-muted)] leading-relaxed mb-4 font-light">
                  {item.excerpt}
                </p>

                {/* Read More Link */}
                <motion.a
                  href="#"
                  className="inline-flex items-center gap-2 text-[var(--gold)] font-semibold group-hover:gap-4 transition-all duration-300"
                  whileHover={{ x: 5 }}
                >
                  Read More
                  <ArrowRight className="w-5 h-5" />
                </motion.a>
              </div>

              </motion.article>
          ))}
        </motion.div>

        {/* View All News CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="text-center mt-12"
        >
          <a
            href="#"
            className="inline-block px-8 py-4 insolip-cta-primary rounded-full"
          >
            View All News
          </a>
        </motion.div>

        {/* Get Started section – blue block, large CTA */}
        <motion.section
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-20 lg:mt-24 rounded-3xl border border-[rgba(201,168,76,0.25)] px-8 py-16 lg:py-20 text-center insolip-card-gradient"
        >
          <h3 className="font-display text-3xl md:text-4xl font-semibold text-white mb-4">
            Ready to get started?
          </h3>
          <p className="text-white/80 text-lg max-w-2xl mx-auto mb-10 font-light">
            Join researchers, innovators, and institutions using INSOLIP to manage and protect innovation.
          </p>
          {onGetStarted ? (
            <button
              onClick={onGetStarted}
              className="inline-flex items-center gap-2 px-12 py-5 text-lg font-semibold rounded-xl bg-[var(--gold)] text-[var(--navy)] hover:opacity-90 hover:shadow-xl transition-all duration-300"
            >
              {content.cta.primary}
              <ArrowRight className="w-6 h-6" />
            </button>
          ) : (
            <a
              href="#contact"
              className="inline-flex items-center gap-2 px-12 py-5 text-lg font-semibold rounded-xl bg-[var(--gold)] text-[var(--navy)] hover:opacity-90 hover:shadow-xl transition-all duration-300"
            >
              {content.cta.primary}
              <ArrowRight className="w-6 h-6" />
            </a>
          )}
        </motion.section>
      </div>
    </section>
  );
}
